import { Component, ElementRef, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { Temporal } from '@js-temporal/polyfill';

import { TranslateService } from '@ngx-translate/core';

import { AppStartupService, LocalWebTicket } from '@ridango/core';
import Env from '@ridango/core/env';

import { BaseTicketComponent } from '../base-ticket/base-ticket.component';

@Component({
    selector: 'ridango-hours-ticket',
    templateUrl: './hours-ticket.component.html'
})
export class HoursTicketComponent extends BaseTicketComponent implements OnInit {
    @Input() ticket: LocalWebTicket;
    @ViewChild('ticketActionsEl') ticketActionsEl: ElementRef;

    isActivatedAtPurchase = false;

    bookletUnitsMapping: any = {
        '=0': 'TICKET_SUMMARY_V2_UNITS_REMAINING',
        '=1': 'TICKET_SUMMARY_V2_UNITS_REMAINING',
        'other': 'TICKET_SUMMARY_V2_UNITS_REMAINING'
    };

    public validityFormatOptions =
        this.env.FORMATTING?.HOURS_TICKET_VALIDITY ?? this.env.FORMATTING?.PERIOD_TICKET_VALIDITY;

    get productType(): string | null {
        return (
            this.ticket?.product_type ??
            this.ticket?.product?.product_type ??
            this.ticket?.product_object?.product_type ??
            null
        );
    }

    get ridangoDateTimeValidityOptions() {
        return {
            format: this.env.PERIOD_TICKET_DATE_FORMAT,
            formatOptions: {
                ...this.validityFormatOptions,
                rangeSeparator: this.env.FORMATTING?.TICKET_VALIDITY_RANGE_SEPARATOR ?? ' - '
            },
            context: 'PERIOD_TICKET_VALIDITY' as const,
            applyTransportDayOffset: false,
            productType: this.productType
        };
    }

    constructor(
        @Inject('ENV') public env: Env,
        public eRef: ElementRef,
        private translate: TranslateService,
        public appStartupService: AppStartupService
    ) {
        super(env, eRef, appStartupService);
    }

    ngOnInit() {
        super.ngOnInit();
        let properties: any;
        if (this.ticket.product_object && this.ticket.product_object.product_properties) {
            properties = this.ticket.product_object.product_properties;
            if (properties && properties.length > 0) {
                this.isActivatedAtPurchase =
                    properties.filter((property) => property.code === 'TICKET_ACTIVATED_AT_PURCHASE').length > 0;
            }
        }
    }

    getZones() {
        if (this.ticket.start_zone_name) {
            if (this.ticket.start_zone_name !== this.ticket.end_zone_name) {
                return this.ticket.start_zone_name + ' › ' + this.ticket.end_zone_name;
            }
            return this.ticket.start_zone_name;
        }
        if (this.ticket.start_zone) {
            if (this.ticket.start_zone.name !== this.ticket.end_zone.name) {
                return this.ticket.start_zone.name + ' › ' + this.ticket.end_zone.name;
            }
            return this.ticket.start_zone.name;
        }
        if (this.ticket.area) {
            if (this.ticket.area.translations) {
                const t = this.ticket.area.translations.find((tt) => tt.lang === this.locale);
                if (t) {
                    return t.translation;
                } else if (this.ticket.area.descr) {
                    return this.ticket.area.descr;
                }
            }
            return this.ticket.area.display;
        }
        if (this.ticket.origin_stop_object) {
            if (
                this.ticket.origin_stop_object.display &&
                this.ticket.destination_stop_object.display &&
                this.ticket.origin_stop_object.display !== this.ticket.destination_stop_object.display
            ) {
                return this.ticket.origin_stop_object.display + ' › ' + this.ticket.destination_stop_object.display;
            } else if (this.ticket.origin_stop_object.display) {
                return this.ticket.origin_stop_object.display;
            }
        }
        return this.translate.instant('TRAVEL_ALL_AREAS');
    }

    get ticketValidityRange(): [Temporal.Instant, Temporal.Instant] {
        const { starting, ending } = this.ticket as { starting: string | Date; ending: string | Date };
        const toInstant = (date: string | Date) =>
            date instanceof Date ? Temporal.Instant.fromEpochMilliseconds(date.getTime()) : Temporal.Instant.from(date);
        return [toInstant(starting), toInstant(ending)];
    }
}
